import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ClarificationChips.css';

/**
 * Typed clarification chips. Renders one chip per suggestion plus an optional
 * "Custom…" chip that expands into a typed input matching value_type.
 * On submit (chip click or custom value), formats the value via
 * user_message_template and calls onSubmit(formattedMessage, options).
 * Suggestions may be strings or {label, value, entity_id} objects. The chip
 * shows label. A project chip sends input_parameters.project_id. A date chip
 * sends input_parameters[field_name] as the ISO date. The message stays the
 * template sentence.
 *
 * @param {Object} props
 * @param {Array} props.fields - [{ field_name, description, value_type, suggestions, allow_custom, user_message_template }]
 * @param {Function} props.onSubmit - Called with (message, { input_parameters }?) 
 * @param {boolean} [props.disabled=false]
 */
const ClarificationChips = ({ fields, onSubmit, disabled = false }) => {
  const { i18n } = useTranslation();
  if (!fields?.length) return null;

  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  const suggestionLabel = (suggestion) => (
    suggestion && typeof suggestion === 'object' ? suggestion.label : suggestion
  );

  const suggestionValue = (suggestion) => {
    if (suggestion && typeof suggestion === 'object') {
      return suggestion.value ?? suggestion.label;
    }
    return suggestion;
  };

  const formatDisplay = (suggestion, value_type) => {
    const value = suggestionLabel(suggestion);
    if (value_type === 'date') {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
        }
      } catch { /* fall through */ }
    }
    return value;
  };

  const submitValue = (template, value, extra) => {
    const message = (template || '{value}').replace('{value}', value);
    if (!disabled && onSubmit) onSubmit(message, extra);
  };

  return (
    <div className="clarification-chips">
      {fields.map((field) => (
        <FieldChips
          key={field.field_name}
          field={field}
          disabled={disabled}
          locale={locale}
          formatDisplay={formatDisplay}
          suggestionLabel={suggestionLabel}
          suggestionValue={suggestionValue}
          submitValue={submitValue}
        />
      ))}
    </div>
  );
};

const FieldChips = ({ field, disabled, locale, formatDisplay, suggestionLabel, suggestionValue, submitValue }) => {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handleChip = (suggestion) => {
    if (suggestion && typeof suggestion === 'object' && suggestion.entity_id != null) {
      const label = suggestionLabel(suggestion);
      submitValue(field.user_message_template, label, {
        input_parameters: { project_id: suggestion.entity_id },
      });
      return;
    }
    const value = suggestionValue(suggestion);
    const extra = field.value_type === 'date'
      ? { input_parameters: { [field.field_name]: value } }
      : undefined;
    submitValue(field.user_message_template, value, extra);
  };

  const handleCustomSubmit = () => {
    if (!customValue.trim()) return;
    const value = customValue.trim();
    const extra = field.value_type === 'date'
      ? { input_parameters: { [field.field_name]: value } }
      : undefined;
    submitValue(field.user_message_template, value, extra);
    setCustomOpen(false);
    setCustomValue('');
  };

  return (
    <div className="clarification-field">
      {field.description && (
        <p className="clarification-field-description">{field.description}</p>
      )}
      <div className="clarification-chip-row">
        {(field.suggestions || []).map((suggestion, idx) => (
          <button
            key={`${field.field_name}-${idx}-${suggestionValue(suggestion)}`}
            type="button"
            className="clarification-chip"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); handleChip(suggestion); }}
          >
            {formatDisplay(suggestion, field.value_type)}
          </button>
        ))}
        {field.allow_custom && !customOpen && (
          <button
            type="button"
            className="clarification-chip clarification-chip-custom"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); setCustomOpen(true); }}
          >
            {t('chat.customEllipsis')}
          </button>
        )}
      </div>
      {field.allow_custom && customOpen && (
        <CustomInput
          fieldType={field.value_type}
          value={customValue}
          setValue={setCustomValue}
          onSubmit={handleCustomSubmit}
          onCancel={() => { setCustomOpen(false); setCustomValue(''); }}
          disabled={disabled}
        />
      )}
    </div>
  );
};

const CustomInput = ({ fieldType, value, setValue, onSubmit, onCancel, disabled }) => {
  const { t } = useTranslation();
  const inputProps = (() => {
    switch (fieldType) {
      case 'date':     return { type: 'date' };
      case 'integer':  return { type: 'number', step: '1' };
      case 'float':    return { type: 'number', step: 'any' };
      case 'boolean':  return { type: 'text' }; // Should not happen (allow_custom=False for booleans).
      default:         return { type: 'text' };
    }
  })();

  return (
    <div className="clarification-custom-input">
      <input
        {...inputProps}
        value={value}
        disabled={disabled}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
      <button
        type="button"
        className="clarification-custom-submit"
        disabled={disabled || !value.trim()}
        onClick={onSubmit}
      >
        {t('chat.submit')}
      </button>
      <button
        type="button"
        className="clarification-custom-cancel"
        disabled={disabled}
        onClick={onCancel}
      >
        {t('chat.cancel')}
      </button>
    </div>
  );
};

export default ClarificationChips;
